<template>
	<div class="preview-panel">
		<v-tabs v-model="activeTab">
			<v-tab value="violations">{{ t('violations') }} ({{ violationsCount }})</v-tab>
			<v-tab value="changes">{{ t('changes') }} ({{ changesCount }})</v-tab>
		</v-tabs>

		<div class="preview-body">
			<ViolationsList v-if="activeTab === 'violations'" :violations="preview?.violations ?? []" />
			<ChangesList v-else />
		</div>
	</div>
</template>

<script setup lang="ts">
import { onBeforeMount, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDutyChangePreview } from '@/rennovation/planning-view/composables/useDutyChangePreview';
import ViolationsList from '@/rennovation/planning-view/components/Layout/Modals/Preview/ViolationsList.vue';
import ChangesList from '@/rennovation/planning-view/components/Layout/Modals/Preview/ChangesList.vue';
import { STORAGE_KEYS } from '@/rennovation/planning-view/constants';
import { usePreviewStore } from '@/rennovation/planning-view/store/usePreviewStore';

type PreviewTab = 'violations' | 'changes';

const previewStore = usePreviewStore();

defineProps<{ preview: DutyChangePreviewResponse | null }>();
defineEmits<{ (e: 'close'): void; (e: 'publish', preview: DutyChangePreviewResponse | null): void }>();

const { t } = useI18n();
const { violationsCount, changesCount } = useDutyChangePreview();

const storedTab = localStorage.getItem(STORAGE_KEYS.PREVIEW_ACTIVE_TAB);
const activeTab = ref<PreviewTab>(storedTab === 'violations' || storedTab === 'changes' ? storedTab : 'changes');

onBeforeMount(() => {
	previewStore.changesPanelActiveTab = activeTab.value;
});

watch(activeTab, (value) => {
	localStorage.setItem(STORAGE_KEYS.PREVIEW_ACTIVE_TAB, value);

	previewStore.changesPanelActiveTab = value;
	previewStore.selectedChangeLog = null;
});
</script>

<style scoped lang="scss">
.preview-panel {
	height: 100%;
	display: flex;
	flex-direction: column;
	overflow: hidden;
	background-color: #f5f5f5;
	border: 3px solid #eeeeee;
}

:deep(.v-tabs) {
	width: 100%;

	.v-slide-group__container {
		width: 100%;
	}

	.v-slide-group__content {
		width: 100%;
		justify-content: stretch;
	}

	.v-tab {
		flex: 1 1 50%;
		max-width: 50%;
		min-width: 0;
		height: auto;
		min-height: 0;
		padding: 21px 25px;

		display: flex;
		align-items: center;
		justify-content: center;

		font-family: Roboto, sans-serif;
		font-weight: 500;
		font-size: 12px;
		text-transform: uppercase;
		color: #0000008a;
	}

	.v-tab--selected {
		color: #10565c;
	}
}

.preview-header {
	padding: 12px;
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	border-bottom: 1px solid #eee;
}

.preview-title {
	font-weight: 700;
	font-size: 16px;
}

.preview-subtitle {
	display: block;
	font-size: 12px;
	color: #666;
}

.preview-body {
	flex: 1;
	min-height: 0;
	overflow-y: auto;
	padding: 8px 8px;
}
</style>
